import { prefersReducedMotion } from '@theme/utilities';

/**
 * Finds text nodes within an element that contain the specified text
 * @param {HTMLElement} element - The element to search
 * @param {string} searchText - The text to find
 * @returns {Array<{node: Text, startIndex: number, endIndex: number}>} Array of matches
 */
function findTextNodes(element, searchText) {
  const matches = [];
  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    null
  );

  let node;
  while ((node = walker.nextNode())) {
    const text = node.textContent;
    const index = text.indexOf(searchText);
    if (index !== -1) {
      matches.push({
        node,
        startIndex: index,
        endIndex: index + searchText.length,
      });
    }
  }

  return matches;
}

/**
 * Wraps text in a node with a span element
 * @param {Text} textNode - The text node to wrap
 * @param {number} startIndex - Start index of text to wrap
 * @param {number} endIndex - End index of text to wrap
 * @param {string} className - Class name for the wrapper
 * @param {Object} dataAttributes - Data attributes to add
 * @returns {HTMLElement} The wrapper element
 */
function wrapTextInNode(textNode, startIndex, endIndex, className, dataAttributes = {}) {
  const text = textNode.textContent;
  const beforeText = text.substring(0, startIndex);
  const wrappedText = text.substring(startIndex, endIndex);
  const afterText = text.substring(endIndex);

  const wrapper = document.createElement('span');
  wrapper.className = className;
  wrapper.textContent = wrappedText;

  Object.entries(dataAttributes).forEach(([key, value]) => {
    wrapper.setAttribute(key, value);
  });

  const parent = textNode.parentNode;
  if (beforeText) {
    parent.insertBefore(document.createTextNode(beforeText), textNode);
  }
  parent.insertBefore(wrapper, textNode);
  if (afterText) {
    parent.insertBefore(document.createTextNode(afterText), textNode);
  }
  parent.removeChild(textNode);

  return wrapper;
}

/**
 * Replaces text in a node with a wrapper element
 * @param {Text} textNode - The text node to replace
 * @param {number} startIndex - Start index of text to replace
 * @param {number} endIndex - End index of text to replace
 * @param {string} initialText - Initial text to display
 * @param {string} className - Class name for the wrapper
 * @param {Object} dataAttributes - Data attributes to add
 * @returns {HTMLElement} The wrapper element
 */
function replaceTextInNode(textNode, startIndex, endIndex, initialText, className, dataAttributes = {}) {
  const text = textNode.textContent;
  const beforeText = text.substring(0, startIndex);
  const afterText = text.substring(endIndex);

  const wrapper = document.createElement('span');
  wrapper.className = className;
  wrapper.textContent = initialText;

  Object.entries(dataAttributes).forEach(([key, value]) => {
    wrapper.setAttribute(key, value);
  });

  const parent = textNode.parentNode;
  if (beforeText) {
    parent.insertBefore(document.createTextNode(beforeText), textNode);
  }
  parent.insertBefore(wrapper, textNode);
  if (afterText) {
    parent.insertBefore(document.createTextNode(afterText), textNode);
  }
  parent.removeChild(textNode);

  return wrapper;
}

/**
 * Color cycling animation handler
 */
class ColorCyclingAnimation {
  constructor(element, colors, duration) {
    this.element = element;
    this.colors = colors;
    this.duration = duration * 1000; // Convert to milliseconds
    this.currentIndex = 0;
    this.animationId = null;
    this.startTime = null;
  }

  start() {
    if (prefersReducedMotion()) {
      // Just set the first color if reduced motion
      this.element.style.color = this.colors[0];
      return;
    }

    this.element.style.transition = `color ${this.duration}ms ease-in-out`;
    this.element.style.color = this.colors[0];
    this.cycle();
  }

  cycle() {
    if (prefersReducedMotion()) return;

    this.currentIndex = (this.currentIndex + 1) % this.colors.length;
    const nextColor = this.colors[this.currentIndex];

    this.element.style.color = nextColor;

    this.animationId = setTimeout(() => {
      this.cycle();
    }, this.duration);
  }

  stop() {
    if (this.animationId) {
      clearTimeout(this.animationId);
      this.animationId = null;
    }
  }
}

/**
 * Typewriter animation handler
 */
class TypewriterAnimation {
  constructor(element, phrases, speed, pause) {
    this.element = element;
    this.phrases = phrases.filter(p => p && p.trim() !== '');
    this.speed = speed;
    this.pause = pause * 1000; // Convert to milliseconds
    this.currentPhraseIndex = 0;
    this.currentText = '';
    this.isTyping = false;
    this.isDeleting = false;
    this.timeoutId = null;
    this.initialText = element.textContent;
  }

  start() {
    if (prefersReducedMotion()) {
      // Just show first phrase if reduced motion
      if (this.phrases.length > 0) {
        this.element.textContent = this.phrases[0];
      }
      return;
    }

    if (this.phrases.length === 0) return;

    this.currentPhraseIndex = 0;
    this.currentText = '';
    this.isTyping = true;
    this.isDeleting = false;
    this.type();
  }

  type() {
    if (prefersReducedMotion()) return;

    const currentPhrase = this.phrases[this.currentPhraseIndex];

    if (this.isDeleting) {
      // Backspace
      this.currentText = currentPhrase.substring(0, this.currentText.length - 1);
      this.element.textContent = this.currentText;

      if (this.currentText.length === 0) {
        // Finished deleting, move to next phrase
        this.isDeleting = false;
        this.currentPhraseIndex = (this.currentPhraseIndex + 1) % this.phrases.length;
        this.timeoutId = setTimeout(() => this.type(), this.pause);
        return;
      }

      this.timeoutId = setTimeout(() => this.type(), this.speed);
    } else {
      // Typing
      this.currentText = currentPhrase.substring(0, this.currentText.length + 1);
      this.element.textContent = this.currentText;

      if (this.currentText.length === currentPhrase.length) {
        // Finished typing, pause then start deleting
        this.isDeleting = true;
        this.timeoutId = setTimeout(() => this.type(), this.pause);
        return;
      }

      this.timeoutId = setTimeout(() => this.type(), this.speed);
    }
  }

  stop() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}

/**
 * Initializes animated heading for a single element
 */
function initAnimatedHeading(element, config) {
  const colorCyclingAnimation = config.enableColorCycling && config.colorCyclingText
    ? initColorCycling(element, config)
    : null;

  const typewriterAnimation = config.enableTypewriter && config.typewriterText
    ? initTypewriter(element, config)
    : null;

  return {
    colorCycling: colorCyclingAnimation,
    typewriter: typewriterAnimation,
    destroy() {
      if (colorCyclingAnimation) colorCyclingAnimation.stop();
      if (typewriterAnimation) typewriterAnimation.stop();
    },
  };
}

/**
 * Finds text in jumbo-text character spans
 * @param {HTMLElement} element - The jumbo-text element
 * @param {string} searchText - The text to find
 * @returns {Array<HTMLElement>} Array of character spans that make up the text
 */
function findTextInJumboText(element, searchText) {
  const chars = searchText.split('');
  const charSpans = Array.from(element.querySelectorAll('.jumbo-text-char'));
  const matches = [];

  for (let i = 0; i <= charSpans.length - chars.length; i++) {
    let match = true;
    const matchedSpans = [];
    
    for (let j = 0; j < chars.length; j++) {
      const span = charSpans[i + j];
      const spanText = span.textContent.trim();
      if (spanText !== chars[j]) {
        match = false;
        break;
      }
      matchedSpans.push(span);
    }
    
    if (match) {
      matches.push(matchedSpans);
      break; // Use first match
    }
  }

  return matches.length > 0 ? matches[0] : null;
}

/**
 * Initializes color cycling animation
 */
function initColorCycling(element, config) {
  // Check if this is jumbo-text - look for jumbo-text element
  const jumboTextElement = element.tagName === 'JUMBO-TEXT' 
    ? element 
    : element.querySelector('jumbo-text') || element.closest('jumbo-text');
  
  if (jumboTextElement) {
    const charSpans = findTextInJumboText(jumboTextElement, config.colorCyclingText);
    if (!charSpans || charSpans.length === 0) return null;

    // Wrap all character spans in a container
    const wrapper = document.createElement('span');
    wrapper.className = 'animated-heading-color-cycle';
    wrapper.setAttribute('data-color-cycle', 'true');
    wrapper.style.setProperty('--color-cycle-duration', `${config.colorCyclingDuration}s`);

    // Set CSS custom properties for colors
    config.colorCyclingColors.forEach((color, index) => {
      wrapper.style.setProperty(`--color-cycle-${index + 1}`, color);
    });

    // Wrap the character spans
    const firstSpan = charSpans[0];
    const parent = firstSpan.parentNode;
    charSpans.forEach(span => {
      wrapper.appendChild(span);
    });

    const animation = new ColorCyclingAnimation(wrapper, config.colorCyclingColors, config.colorCyclingDuration);
    animation.start();

    return animation;
  } else {
    const matches = findTextNodes(element, config.colorCyclingText);
    if (matches.length === 0) return null;

    // Use the first match
    const match = matches[0];
    const wrapper = wrapTextInNode(
      match.node,
      match.startIndex,
      match.endIndex,
      'animated-heading-color-cycle',
      {
        'data-color-cycle': 'true',
        style: `--color-cycle-duration: ${config.colorCyclingDuration}s;`,
      }
    );

    // Set CSS custom properties for colors
    config.colorCyclingColors.forEach((color, index) => {
      wrapper.style.setProperty(`--color-cycle-${index + 1}`, color);
    });

    const animation = new ColorCyclingAnimation(wrapper, config.colorCyclingColors, config.colorCyclingDuration);
    animation.start();

    return animation;
  }
}

/**
 * Initializes typewriter animation
 */
function initTypewriter(element, config) {
  // Check if this is jumbo-text - look for jumbo-text element
  const jumboTextElement = element.tagName === 'JUMBO-TEXT' 
    ? element 
    : element.querySelector('jumbo-text') || element.closest('jumbo-text');
  
  if (jumboTextElement) {
    // For jumbo-text, we need to replace character spans
    const charSpans = findTextInJumboText(jumboTextElement, config.typewriterText);
    if (!charSpans || charSpans.length === 0) return null;

    // Create wrapper and replace character spans
    const wrapper = document.createElement('span');
    wrapper.className = 'animated-heading-typewriter';
    wrapper.setAttribute('data-typewriter', 'true');
    wrapper.textContent = config.typewriterText;

    const firstSpan = charSpans[0];
    const parent = firstSpan.parentNode;
    
    // Remove old spans and insert wrapper
    charSpans.forEach(span => {
      parent.removeChild(span);
    });
    parent.insertBefore(wrapper, firstSpan);

    const animation = new TypewriterAnimation(
      wrapper,
      config.typewriterPhrases,
      config.typewriterSpeed,
      config.typewriterPause
    );
    animation.start();

    return animation;
  } else {
    const matches = findTextNodes(element, config.typewriterText);
    if (matches.length === 0) return null;

    // Use the first match
    const match = matches[0];
    const initialText = config.typewriterText;
    const wrapper = replaceTextInNode(
      match.node,
      match.startIndex,
      match.endIndex,
      initialText,
      'animated-heading-typewriter',
      {
        'data-typewriter': 'true',
      }
    );

    const animation = new TypewriterAnimation(
      wrapper,
      config.typewriterPhrases,
      config.typewriterSpeed,
      config.typewriterPause
    );
    animation.start();

    return animation;
  }
}

/**
 * Main initialization function
 */
function initAnimatedHeadings() {
  if (prefersReducedMotion()) {
    // Still initialize but animations will be disabled
  }

  const elements = document.querySelectorAll('[data-animated-heading="true"]');
  const animations = new Map();

  elements.forEach((element) => {
    // Find config script using block ID from data attribute
    const blockId = element.getAttribute('data-animated-heading-block-id');
    if (!blockId) return;

    const configScript = document.querySelector(`script[data-animated-heading-config="${blockId}"]`);
    if (!configScript) return;

    // For jumbo-text, wait for it to be ready
    const isJumboText = element.tagName === 'JUMBO-TEXT';
    if (isJumboText && !element.classList.contains('ready')) {
      // Wait for jumbo-text to be ready
      const observer = new MutationObserver((mutations, obs) => {
        if (element.classList.contains('ready')) {
          obs.disconnect();
          try {
            const config = JSON.parse(configScript.textContent);
            const animation = initAnimatedHeading(element, config);
            if (animation) {
              animations.set(element, animation);
            }
          } catch (error) {
            console.error('Failed to parse animated heading config:', error);
          }
        }
      });
      observer.observe(element, { attributes: true, attributeFilter: ['class'] });
      return;
    }

    try {
      const config = JSON.parse(configScript.textContent);
      const animation = initAnimatedHeading(element, config);
      if (animation) {
        animations.set(element, animation);
      }
    } catch (error) {
      console.error('Failed to parse animated heading config:', error);
    }
  });

  return animations;
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAnimatedHeadings);
} else {
  initAnimatedHeadings();
}

// Re-initialize for dynamically added content (e.g., theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    setTimeout(initAnimatedHeadings, 100);
  });
}

export { initAnimatedHeadings, ColorCyclingAnimation, TypewriterAnimation };

